import type { CSSProperties } from 'react';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { Link } from 'react-router-dom';
import { CircleCheck, Circle, Inbox } from 'lucide-react';

import {
  DashboardCard,
  FailedSections,
  LineChartCard,
  type LineChartCardProps,
  SECTION_FAILED,
  StatTile,
  StatTileSkeleton,
  StatsRow,
} from '~/components/dashboard';
import {
  Card,
  CardHeader,
  CardSection,
  StatusChip,
  EmptyState,
  PanelTable,
  type PanelTableColumn,
} from '~/components/ui';
import { useUrlParams } from '~/hooks/useUrlParams';
import {
  useGetDashboardFlagsQuery,
  useGetDashboardRequestsQuery,
  useGetDashboardUpdatesQuery,
  useListFlagsQuery,
  useListSdkKeysQuery,
} from '~/modules/query/featureFlags';

import {
  REQUEST_STATUSES,
  REQUEST_STATUS_LABELS,
  REQUEST_STATUS_TONES,
} from '~/components/Constants/featureFlagsConstants';
import { Select } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type {
  DashboardFlags,
  DashboardRequests,
  DashboardUpdates,
} from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';
import { formatLocalTime } from '~/lib/datetime';
import { formatPercent, plural } from '~/lib/format';
import { backlogSeverity } from '~/lib/severity';

const dayLabel = (date: string) => formatLocalTime(date, { month: 'short', day: 'numeric' });

type FlagRow = DashboardFlags['flags'][number];
type UpdateRow = DashboardUpdates['updates'][number];

const LINK_CLASS =
  'rounded-sm font-medium text-brand-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:text-brand-400 dark:focus-visible:ring-offset-gray-900';

/** Share of `total` that is `on`, as a bar and a one-decimal percent (DESIGN 7.3). */
const AdoptionBar = ({ on, total }: { on: number; total: number }) => {
  const pct = total > 0 ? Math.min(100, (on / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <div
        className="h-2 w-20 shrink-0 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
        role="img"
        aria-label={`${formatPercent(pct)} on`}
      >
        <div
          className="h-full w-[var(--w)] bg-success-500"
          style={{ '--w': `${pct}%` } as CSSProperties}
        />
      </div>
      <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
        {formatPercent(pct)}
      </span>
    </div>
  );
};

const FLAG_COLUMNS: PanelTableColumn<FlagRow>[] = [
  {
    header: 'Flag',
    render: (f) => (
      <Link
        to={routes.featureFlagsRecord('flags', f.key)}
        className={`${LINK_CLASS} whitespace-nowrap`}
      >
        {f.key}
      </Link>
    ),
  },
  { header: 'On', render: (f) => f.on },
  { header: 'Off', render: (f) => f.off },
  { header: '% on', render: (f) => <AdoptionBar on={f.on} total={f.on + f.off} /> },
];

const UPDATE_COLUMNS: PanelTableColumn<UpdateRow>[] = [
  {
    header: 'Update',
    render: (u) => (
      <Link
        to={routes.featureFlagsRecord('updates', u.id)}
        className={`${LINK_CLASS} whitespace-nowrap`}
      >
        {u.title || u.key}
      </Link>
    ),
  },
  { header: 'Eligible', render: (u) => u.eligible },
  { header: 'On', render: (u) => u.on },
  { header: 'Off (default)', render: (u) => u.offByDefault },
  { header: 'Opted out', render: (u) => u.optedOut },
  { header: '% on', render: (u) => <AdoptionBar on={u.on} total={u.eligible} /> },
];

/** The empty install: one checklist instead of a dashboard of zeros. */
const GetStarted = ({ reference }: { reference: Reference }) => {
  const flags = useListFlagsQuery();
  const keys = useListSdkKeysQuery();
  const steps = [
    {
      label: 'Add an environment',
      hint: 'Where flags are switched on or off, such as production.',
      done: reference.environments.length > 0,
      href: routes.featureFlagsAdmin('environments'),
    },
    {
      label: 'Define an attribute',
      hint: 'What you know about a user, so flags can target them.',
      done: reference.attributes.length > 0,
      href: routes.featureFlagsAdmin('attributes'),
    },
    {
      label: 'Add a product',
      hint: 'The app that shows updates and collects feature requests.',
      done: reference.products.length > 0,
      href: routes.featureFlagsAdmin('products'),
    },
    {
      label: 'Create a flag',
      hint: 'The first thing to switch on or off.',
      done: (flags.data?.length ?? 0) > 0,
      href: routes.featureFlagsSection('flags'),
    },
    {
      label: 'Generate an SDK key',
      hint: 'Lets your app read its flags.',
      done: (keys.data?.length ?? 0) > 0,
      href: routes.featureFlagsAdmin('sdk-keys'),
    },
  ];
  return (
    <Card>
      <CardHeader
        title="Get started"
        description="Five steps from an empty install to a flag your app can read."
      />
      <ol className="space-y-3 p-5 md:p-6">
        {steps.map((s) => (
          <li key={s.href}>
            <Link
              to={s.href}
              className="flex items-center gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:focus-visible:ring-offset-gray-900"
            >
              {s.done ? (
                <CircleCheck
                  className="size-5 shrink-0 text-success-600 dark:text-success-500"
                  aria-label="Done"
                />
              ) : (
                <Circle
                  className="size-5 shrink-0 text-gray-400 dark:text-gray-500"
                  aria-label="Not done yet"
                />
              )}
              <span>
                <span className="block text-sm font-medium text-gray-800 dark:text-white/90">
                  {s.label}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{s.hint}</span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
};

const RequestsBody = ({
  data,
  productName,
}: {
  data: DashboardRequests;
  productName: (key: string) => string;
}) => {
  const { totals, byStatus, top } = data;
  if (totals.requests === 0) {
    return (
      <EmptyState
        icon={<Inbox className="size-8" aria-hidden="true" />}
        title="No feature requests yet"
        description="Requests your users submit show up here, with the most voted first."
      />
    );
  }
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {plural(totals.requests, 'request')} • {plural(totals.votes, 'vote')}
      </p>
      <div className="flex flex-wrap gap-2">
        {REQUEST_STATUSES.filter((s) => (byStatus[s] ?? 0) > 0).map((s) => (
          <StatusChip
            key={s}
            label={`${REQUEST_STATUS_LABELS[s]} ${byStatus[s]}`}
            tone={REQUEST_STATUS_TONES[s]}
          />
        ))}
      </div>
      <CardSection title="Top voted">
        <ol className="custom-scrollbar max-h-80 space-y-2 overflow-y-auto pr-1">
          {top.map((r, i) => (
            <li key={`${r.product}:${r.id}`}>
              <Link
                to={`${routes.featureFlagsRecord('requests', r.id)}?product=${encodeURIComponent(r.product)}`}
                className="flex items-center justify-between rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:focus-visible:ring-offset-gray-900"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-800 dark:text-white/90">
                    #{i + 1} {r.title}
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {productName(r.product)} • {REQUEST_STATUS_LABELS[r.status]}
                  </span>
                </span>
                <span className="ml-3 shrink-0 text-base font-semibold text-gray-800 dark:text-white/90">
                  {r.votes}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </CardSection>
    </div>
  );
};

const Dashboard = ({
  reference,
  environment,
  product,
}: {
  reference: Reference;
  environment: string;
  product: string;
}) => {
  const flags = useGetDashboardFlagsQuery({ environment, product: product || undefined });
  const updates = useGetDashboardUpdatesQuery({ environment, product: product || undefined });
  const requests = useGetDashboardRequestsQuery({ product: product || undefined });
  const productName = (key: string) => reference.products.find((p) => p.key === key)?.name ?? key;
  const failedQueries = [
    { name: 'flag adoption', query: flags },
    { name: 'product update adoption', query: updates },
    { name: 'feature requests', query: requests },
  ].filter(({ query }) => query.isError && !query.data);

  const series: LineChartCardProps['series'] | undefined = requests.data && [
    {
      id: 'Submitted',
      data: requests.data.daily.map((d) => ({ x: dayLabel(d.date), y: d.submitted })),
    },
    { id: 'Votes', data: requests.data.daily.map((d) => ({ x: dayLabel(d.date), y: d.votes })) },
  ];
  const awaiting = requests.data?.totals.awaitingReview ?? 0;
  // Awaiting review = not yet approved, which the service keeps in step with status `submitted`.
  const requestsHref = `${routes.featureFlagsSection('requests')}?status=submitted${product ? `&product=${encodeURIComponent(product)}` : ''}`;
  const flagsLoading = flags.isLoading;
  const updatesLoading = updates.isLoading;
  const requestsLoading = requests.isLoading;

  return (
    <section aria-label="Feature flags dashboard" className="grid grid-cols-12 gap-4 md:gap-6">
      <div className="col-span-12">
        <FailedSections
          failed={failedQueries.map(({ name }) => name)}
          message={apiErrorMessage(failedQueries[0]?.query.error as ApiErrorInput)}
          onRetry={() => failedQueries.forEach(({ query }) => query.refetch())}
          testId="feature-flags-overview"
        />
      </div>

      <div className="col-span-12">
        <StatsRow ariaLabel="Feature flag totals">
          {flagsLoading ? (
            <>
              <StatTileSkeleton />
              <StatTileSkeleton />
            </>
          ) : (
            <>
              <StatTile
                label="Known users"
                value={flags.data?.knownUsers}
                href={routes.featureFlagsSection('users')}
              />
              <StatTile
                label="Active flags"
                value={flags.data?.flags.length}
                href={routes.featureFlagsSection('flags')}
              />
            </>
          )}
          {updatesLoading ? (
            <StatTileSkeleton />
          ) : (
            <StatTile
              label="Updates live"
              value={updates.data?.updates.length}
              href={routes.featureFlagsSection('updates')}
            />
          )}
          {requestsLoading ? (
            <StatTileSkeleton />
          ) : (
            <StatTile
              label="Requests to review"
              value={requests.data?.totals.awaitingReview}
              href={requestsHref}
              severity={backlogSeverity(awaiting)}
            />
          )}
        </StatsRow>
      </div>

      <DashboardCard
        className="col-span-12 lg:col-span-6"
        loading={flagsLoading}
        error={flags.error && !flags.data ? SECTION_FAILED : undefined}
        title="Flag adoption"
      >
        {flags.data?.flags.length ? (
          <PanelTable
            columns={FLAG_COLUMNS}
            data={flags.data.flags}
            getRowId={(f) => f.key}
            maxHeight="24rem"
            ariaLabel="Flag adoption"
            emptyMessage="No flags yet."
            emptyDescription="Adoption shows here once a flag exists."
          />
        ) : (
          <EmptyState
            title="No flags yet"
            description="Create a flag and its adoption shows up here."
            action={
              <Link to={routes.featureFlagsSection('flags')} className={LINK_CLASS}>
                Go to flags
              </Link>
            }
          />
        )}
      </DashboardCard>

      <DashboardCard
        className="col-span-12 lg:col-span-6"
        loading={updatesLoading}
        error={updates.error && !updates.data ? SECTION_FAILED : undefined}
        title="Product update adoption"
      >
        {updates.data?.updates.length ? (
          <PanelTable
            columns={UPDATE_COLUMNS}
            data={updates.data.updates}
            getRowId={(u) => u.id}
            maxHeight="24rem"
            ariaLabel="Product update adoption"
            emptyMessage="No product updates yet."
            emptyDescription="Adoption shows here once a product update is published."
          />
        ) : (
          <EmptyState
            title="No product updates live"
            description="Publish a product update and who has it on shows up here."
            action={
              <Link to={routes.featureFlagsSection('updates')} className={LINK_CLASS}>
                Go to product updates
              </Link>
            }
          />
        )}
      </DashboardCard>

      <DashboardCard
        className="col-span-12 lg:col-span-5"
        loading={requestsLoading}
        error={requests.error && !requests.data ? SECTION_FAILED : undefined}
        title="Feature requests"
      >
        {requests.data && <RequestsBody data={requests.data} productName={productName} />}
      </DashboardCard>

      <LineChartCard
        className="col-span-12 lg:col-span-7"
        title="Submissions & votes, last 30 days"
        ariaLabel="Feature request submissions and votes per day, last 30 days"
        loading={requestsLoading}
        series={series ?? []}
        integers
        seriesTokens={['brand-500', 'orange-500']}
        error={requests.error && !requests.data ? SECTION_FAILED : undefined}
        emptyTitle="No activity in the last 30 days"
        emptyDescription="Feature requests and votes appear here as your users send them."
      />
    </section>
  );
};

export const OverviewTab = ({ reference }: { reference: Reference }) => {
  const url = useUrlParams();
  const { environments, products } = reference;
  if (environments.length === 0) return <GetStarted reference={reference} />;

  const environment =
    environments.find((e) => e.key === url.get('environment'))?.key ?? environments[0].key;
  const product = products.find((p) => p.key === url.get('product'))?.key ?? '';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <Select
          className="w-56"
          id="overview-environment"
          label="Environment"
          value={environment}
          options={environments.map((e) => ({ value: e.key, label: e.name }))}
          onChange={(v) => url.set({ environment: v })}
        />
        <Select
          className="w-56"
          id="overview-product"
          label="Product"
          value={product}
          options={products.map((p) => ({ value: p.key, label: p.name }))}
          placeholder="All products"
          placeholderSelectable
          onChange={(v) => url.set({ product: v || null })}
        />
      </div>
      <Dashboard reference={reference} environment={environment} product={product} />
    </div>
  );
};

export default OverviewTab;
